import { useState } from "react";
import { Upload, BookCheck, GalleryVerticalEnd, ChartNoAxesCombined } from "lucide-react";

const RobotIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    fill="currentColor"
    viewBox="0 0 16 16"
    className={className}
  >
    <path d="M6 12.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5M3 8.062C3 6.76 4.235 5.765 5.53 5.886a26.6 26.6 0 0 0 4.94 0C11.765 5.765 13 6.76 13 8.062v1.157a.93.93 0 0 1-.765.935c-.845.147-2.34.346-4.235.346s-3.39-.2-4.235-.346A.93.93 0 0 1 3 9.219zm4.542-.827a.25.25 0 0 0-.217.068l-.92.9a25 25 0 0 1-1.871-.183.25.25 0 0 0-.068.495c.55.076 1.232.149 2.02.193a.25.25 0 0 0 .189-.071l.754-.736.847 1.71a.25.25 0 0 0 .404.062l.932-.97a25 25 0 0 0 1.922-.188.25.25 0 0 0-.068-.495c-.538.074-1.207.145-1.98.189a.25.25 0 0 0-.166.076l-.754.785-.842-1.7a.25.25 0 0 0-.182-.135" />
    <path d="M8.5 1.866a1 1 0 1 0-1 0V3h-2A4.5 4.5 0 0 0 1 7.5V8a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1v1a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-1a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1v-.5A4.5 4.5 0 0 0 10.5 3h-2zM14 7.5V13a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7.5A3.5 3.5 0 0 1 5.5 4h5A3.5 3.5 0 0 1 14 7.5" />
  </svg>
);
import { FeatureVideo } from "./FeatureVideo";

const features = [
  {
    id: "upload_paste_record",
    name: "Add Content",
    icon: Upload,
    title: "Drop It In. Get a Complete Study Pack in Seconds",
    description:
      "Lecture slides. YouTube videos. Recorded classes. Past papers. Shattara turns them all into summaries, flashcards, and quizzes — personalized to what you're actually studying.",
    badge: "UPLOAD/PASTE/RECORD",
    videoMp4: "/assets/shattaraai_smartcontent_feature.mp4",
    videoAlt: "Demo showing PDF upload and automatic summary generation",
  },
  {
    id: "ai-tutor",
    name: "AI Tutor",
    icon: RobotIcon,
    title: "Ask Anything. Get It Explained Until You Actually Get It",
    description:
      "Stuck on a concept at midnight? Shattara explains it step by step — in plain language, without judgment, without making you feel behind. Ask once, ask ten times. It never gets impatient.",
    badge: "YOUR PERSONAL TUTOR",
    videoMp4: "/assets/shattaraai_aitutor_feature.mp4",
    videoAlt: "Demo of AI tutor answering student questions in real-time",
  },
  {
    id: "quizzes_exams",
    name: "Quizzes & Exams",
    icon: BookCheck,
    title: "Find Your Weak Spots Before the Exam Does",
    description:
      "Shattara builds quizzes directly from your materials — so you're practicing exactly what you'll be tested on, not generic questions from the internet. See where you're weak. Fix it before results day.",
    badge: "EXAM PREP",
    videoMp4: "/assets/shattaraai_quizzess_feature.mp4",
    videoAlt: "Demo of quiz generation and exam practice features",
  },
  {
    id: "personalized_flashcards",
    name: "Flashcards",
    icon: GalleryVerticalEnd,
    title: "Your Notes. Your Videos. Turned Into Flashcards That Actually Stick",
    description:
      "Upload a YouTube URL, a PDF, or your own notes. Shattara pulls out the key concepts and builds a flashcard deck tailored to your exact syllabus — not someone else's version of the same topic.",
    badge: "MADE FOR YOUR MATERIAL",
    videoMp4: "/assets/shattaraai_flashcards_feature.mp4",
    videoAlt: "Demo of personalized flashcard creation from study materials",
  },
  {
    id: "progress_insights",
    name: "Reports",
    icon: ChartNoAxesCombined,
    title: "Stop Guessing If You're Ready. Know It",
    description:
      "Shattara tracks every quiz, every session, every gap and shows you exactly which topics need more work before your exam. No more studying for 4 hours and feeling unsure. Walk into your exam knowing your weak spots are covered.",
    badge: "YOUR STUDY REPORT CARD",
    videoMp4: "/assets/shattaraai_insights_feature.mp4",
    videoAlt: "Demo of progress tracking dashboard and analytics",
  },
];

export const Features = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <section id="features" className="w-full py-20 px-4 md:px-16 flex items-center justify-center">
      <div className="max-w-[1200px] w-full flex flex-col gap-10">
        {/* Header */}
        <div className="mx-auto w-full">
          <div className="flex flex-col gap-5">
            <h2 className="text-4xl leading-none text-foreground text-center font-medium md:text-6xl">
              The Study Partner That Actually Gets You
            </h2>
            <p className="text-muted-foreground font-medium max-w-[37.5rem] mx-auto text-center">
              Upload what you're studying. Shattara reads it, explains it, quizzes you on it and tells you exactly where
              you need more work. No fluff. No wasted time.
            </p>
          </div>
        </div>

        {/* Tabs and Content */}
        <div className="flex flex-col gap-3">
          {/* Tab Navigation */}
          <div role="tablist" aria-label="Product features" className="bg-muted rounded-3xl p-2 flex gap-2 overflow-x-auto scrollbar-hide">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <button
                  key={feature.id}
                  role="tab"
                  aria-selected={activeTab === index}
                  aria-controls={`tabpanel-${feature.id}`}
                  id={`tab-${feature.id}`}
                  onClick={() => setActiveTab(index)}
                  className={`flex-1 min-w-fit flex items-center justify-center gap-2 rounded-3xl p-4 transition-all ${activeTab === index ? "bg-card shadow-sm opacity-100" : "opacity-50 hover:opacity-75"}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-lg whitespace-nowrap">{feature.name}</span>
                </button>
              );
            })}
          </div>

          {/* Content Area */}
          <div role="tabpanel" id={`tabpanel-${features[activeTab].id}`} aria-labelledby={`tab-${features[activeTab].id}`} className="bg-card/60 backdrop-blur-sm rounded-3xl overflow-hidden">
            <div className="flex flex-col lg:flex-row gap-10 p-2 md:py-2 md:pl-2 md:pr-10">
              {/* Left Side - Video Demo */}
              <div className="flex-1 relative rounded-2xl overflow-hidden min-h-[384px] bg-gradient-to-br from-muted to-muted/50">
                {features.map((feature, index) => (
                  <FeatureVideo
                    key={feature.id}
                    mp4Src={feature.videoMp4}
                    alt={feature.videoAlt}
                    isActive={activeTab === index}
                    className={`transition-opacity duration-300 ${
                      activeTab === index ? "opacity-100" : "opacity-0 pointer-events-none"
                    }`}
                  />
                ))}
              </div>

              {/* Right Side - Description */}
              <div className="lg:w-96 flex flex-col gap-6 justify-start py-6">
                <div className="inline-block w-fit">
                  <div className="bg-brand-yellow/20 px-3 py-2 rounded-3xl">
                    <p className="text-xs font-medium uppercase text-foreground">{features[activeTab].badge}</p>
                  </div>
                </div>
                <h3 className="text-3xl md:text-4xl font-normal text-foreground">{features[activeTab].title}</h3>
                <p className="text-muted-foreground leading-relaxed">{features[activeTab].description}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
